import { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { supabase } from '@/integrations/supabase/client';

interface MapEmbedProps {
  address?: string;
  city?: string;
  postalCode?: string;
  latitude?: number | null;
  longitude?: number | null;
  editable?: boolean;
  onLocationChange?: (lat: number, lng: number) => void;
  className?: string;
}

export default function MapEmbed({ 
  address, 
  city, 
  postalCode, 
  latitude, 
  longitude, 
  editable = false, 
  onLocationChange,
  className = "" 
}: MapEmbedProps) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const marker = useRef<mapboxgl.Marker | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const initialized = useRef(false);

  useEffect(() => {
    if (!mapContainer.current) return;

    const initializeMap = async () => {
      try {
        // Double check container still exists
        if (!mapContainer.current) {
          throw new Error('Map container not available');
        }
        // Fetch Mapbox token from edge function
        const { data, error: tokenError } = await supabase.functions.invoke('get-mapbox-token');
        
        if (tokenError) throw tokenError;
        if (!data?.token) throw new Error('No Mapbox token received');

        mapboxgl.accessToken = data.token;

        let lng: number;
        let lat: number;

        // Priority: use saved coordinates if available, otherwise geocode, otherwise use default UK center
        if (latitude !== null && longitude !== null && latitude !== undefined && longitude !== undefined) {
          lng = longitude;
          lat = latitude;
        } else {
          // Geocode the address if provided
          const searchQuery = [address, city, postalCode].filter(Boolean).join(', ');
          if (searchQuery) {
            const geocodeUrl = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(searchQuery)}.json?access_token=${data.token}`;
            
            const geocodeResponse = await fetch(geocodeUrl);
            const geocodeData = await geocodeResponse.json();

            if (geocodeData.features && geocodeData.features.length > 0) {
              [lng, lat] = geocodeData.features[0].center;
            } else {
              // Default to center of UK if geocoding fails
              lng = -2.5;
              lat = 54.0;
            }
          } else {
            // No address provided - default to center of UK
            lng = -2.5;
            lat = 54.0;
          }
        }

        // Initialize map - ensure container exists
        if (!mapContainer.current) {
          throw new Error('Map container lost during initialization');
        }

        map.current = new mapboxgl.Map({
          container: mapContainer.current,
          style: 'mapbox://styles/mapbox/streets-v12',
          center: [lng, lat],
          zoom: 14,
        });

        // Mark as successfully initialized
        initialized.current = true;

        // Add navigation controls
        map.current.addControl(
          new mapboxgl.NavigationControl(),
          'top-right'
        );

        // Add marker
        marker.current = new mapboxgl.Marker({ 
          color: '#4CAF50',
          draggable: editable 
        })
          .setLngLat([lng, lat])
          .addTo(map.current);

        // Handle marker drag if editable
        if (editable && onLocationChange) {
          marker.current.on('dragend', () => {
            const lngLat = marker.current!.getLngLat();
            onLocationChange(lngLat.lat, lngLat.lng);
          });
        }

        setLoading(false);
      } catch (err) {
        console.error('Error initializing map:', err);
        setError(err instanceof Error ? err.message : 'Failed to load map');
        setLoading(false);
      }
    };

    initializeMap();

    // Cleanup
    return () => {
      if (initialized.current && map.current) {
        try {
          marker.current?.remove();
          map.current.remove();
          map.current = null;
          marker.current = null;
          initialized.current = false;
        } catch (err) {
          console.error('Error cleaning up map:', err);
        }
      }
    };
  }, [address, city, postalCode, latitude, longitude, editable, onLocationChange]);

  if (error) {
    return (
      <div className={`flex items-center justify-center bg-muted rounded-lg ${className}`}>
        <p className="text-muted-foreground">Map unavailable</p>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted rounded-lg z-10">
          <p className="text-muted-foreground">Loading map...</p>
        </div>
      )}
      <div ref={mapContainer} className="w-full h-full rounded-lg" />
    </div>
  );
}
